import store from '~/store';
import type { IUserInfo, ILoginParams, IRegistParams } from '~/services/UserServer/types';
import type { EnterpriseInfo } from '~/services/AccountServer/types';
import type { IRoleRights  } from '~/services/BasicServer/types';
import { Roles } from '~/services/BasicServer/types';
import UserServer from '~/services/UserServer';
import BasicServer from '~/services/BasicServer';
import TranslateServer from '~/services/TranslateServer';
import AccountServer from '~/services/AccountServer';
import { NetCode, Token } from '~/types/const';
import useBasicStore from './basic';
import { useCrossDomainCookie } from "@/composables/useCrossDomainCookie";
const { createCrossDomainCookie } = useCrossDomainCookie();

const useUserStore = defineStore('user', ()=>{
    const token = ref("")
    const isGetBizTeamInfo = ref(false)
    // 标记 userInfo 是否已从服务端获取过，防止 persist 恢复的旧 bizTeamId 被注入请求头
    const isUserInfoReady = ref(false)
    const translateAICheckEngines = ref([])
    async function login(params: ILoginParams, callback: ()=>void, errorCallback: (message: string, code?: string)=>void){
        let res = await UserServer.login<string>(params)
        if(res.code === NetCode.SUCCESS){
            token.value = res.data as string
            createCrossDomainCookie(Token, res.data as string)
            setTimeout(()=> getUserInfo())
            setTimeout(()=> callback())
        }else{
            errorCallback(res.message, res.code)
        }
    }

    async function getAccountRegist(params: IRegistParams, callback: ()=>void, errorCallback: (message: string, code?: string)=>void){
        let res = await UserServer.getAccountRegist(params)
        if(res.code === NetCode.SUCCESS){
            token.value = res.data as string
            createCrossDomainCookie(Token, res.data as string)
            setTimeout(()=> getUserInfo())
            setTimeout(()=> callback())
        }else{
            errorCallback(res.message, res.code)
        }
    }
    function tokenFail(){
        token.value = ""
        isGetBizTeamInfo.value = false
        createCrossDomainCookie(Token)
        // useCookie(Token).value = ""
        userInfo.value = {} as IUserInfo
        bizTeamInfo.value = {} as EnterpriseInfo
        localStorage.setItem("user", JSON.stringify({}))
        localStorage.removeItem("user_token")
    }

    const userInfo = ref<IUserInfo>({} as IUserInfo)
    async function getLocalUserInfo(){
        let user = localStorage.getItem("user")
        if(user){
            let userData = JSON.parse(user)
            if(userData.userInfo){
                userInfo.value = userData.userInfo
            }
            if(userData.roleRightsMap){
                roleRightsMap.value = userData.roleRightsMap
            }
            if(userData.token){
                token.value = userData.token
            }
        }
    }
    async function getUserInfo(){
        let res = await UserServer.getUserInfo<IUserInfo>()
        if(res.code === NetCode.SUCCESS){
            userInfo.value = res.data as IUserInfo
            isUserInfoReady.value = true
            // 这里如果用户加入企业， 并且没有企业信息 请求一次企业信息
            if (userInfo.value.bizTeamId && (!bizTeamInfo.value || !bizTeamInfo.value.id) && !isGetBizTeamInfo.value) {
                console.log("getBizTeamInfo===", bizTeamInfo.value)
                isGetBizTeamInfo.value = true
                getBizTeamInfo()
            }else if (!userInfo.value.bizTeamId) {
                // 用户已不在企业中（解散/退出），清空企业信息
                bizTeamInfo.value = {} as EnterpriseInfo
                isGetBizTeamInfo.value = false
            }
        }
    }

    // 企业相关信息
    const bizTeamInfo: Ref<EnterpriseInfo> = ref({} as EnterpriseInfo)
    async function getBizTeamInfo(){
        let res = await AccountServer.getEnterpriseInfo()
        if(res.code === NetCode.SUCCESS){
            bizTeamInfo.value = res.data as EnterpriseInfo
        }
    }

    const roleRightsMap = ref<Record<Roles, IRoleRights> | null>(null)
    async function getRoleRights(){
        let res = await BasicServer.getRoleRights()
        if(res.code === NetCode.SUCCESS){
            roleRightsMap.value = res.data as Record<Roles, IRoleRights>
        }
    }
    const translateEngines = ref<any[]>([])
    const translateEngineMap = ref<Record<string, any>>({})
    async function getTranslateEngines(){
        let res = await TranslateServer.getRequestEngines()
        if(res.code === NetCode.SUCCESS){
            translateEngineMap.value = res.data || {}
            let engines: any[] = []
            Object.keys(translateEngineMap.value).forEach((key)=>{
                let item = translateEngineMap.value[key]
                item.engineDescribe = `${key}-describe`.replaceAll('-', '_')
                // 仅当后端显式返回 canOcr 时才规整为数字；未返回则保持原值(undefined)，由消费方按「未标记=支持」处理，
                // 避免后端尚未给某些引擎(如企业/vip默认引擎)下发 canOcr 时被误判为不支持而置灰。
                if (item.canOcr !== undefined && item.canOcr !== null && item.canOcr !== '') {
                    item.canOcr = Number(item.canOcr) === 1 ? 1 : 0
                }
                if (item.showFlag != 0){
                    engines.push(item)
                }
            })
            translateEngines.value = engines.sort((a, b) => a.showFlag - b.showFlag)
        }
    }

    // 统一收口引擎的 OCR/图片翻译能力判断，避免页面和组件各自解释 canOcr。
    // isAICheck=true 时查 AI 校对引擎列表。
    // 口径：仅当后端显式下发 canOcr===0 才判定「不支持」；未下发(undefined/null)视为支持(向后兼容)。
    function getTranslateEngineCanOcr(engineId: number | string, isAICheck: boolean = false): boolean {
        if (engineId === null || engineId === undefined || engineId === '') {
            return false
        }
        const engineList = isAICheck ? translateAICheckEngines.value : translateEngines.value
        const currentEngine = (engineList || []).find((item: any) => Number(item.engineId) === Number(engineId))
        if (!currentEngine) {
            return false
        }
        const canOcr = currentEngine.canOcr
        return !(canOcr !== undefined && canOcr !== null && canOcr !== '' && Number(canOcr) === 0)
    }

    async function logout(callback: ()=>void){
        tokenFail()
        callback()
    }
    // 监听 homePageConfig 的变化
    // watch(() => useBasicStore().homePageConfig, (newVal) => {
    //     console.log("userRights.value", userRights.value)
    // }, { deep: true })

    const userRights = computed(()=>{
        // 用户角色 roleRightsMap
        const basicStore = useBasicStore()
        const { homePageConfig } = storeToRefs(basicStore)
        // 定义角色 普通用户加油包的时候 获取vip的相关权益
        const userRole = formatUseBiz(bizTeamInfo.value)
        let map = {
            ...userInfo.value,
            ...roleRightsMap.value?.[userRole],
            ...homePageConfig.value,
        }
        map.batchUploadLimit = map.batchUploadLimit || 1
        return map
    })

    const userRemark = computed(() => {
        if (userInfo.value?.remark) {
            return JSON.parse(userInfo.value?.remark)
        }
        return null
    })

    // 检查用户是否能上传
    function getCheckUserUpload(){
        return true
    }

    // type = 0 试看 type = 1 高级引擎 type = 2 推理引擎
    function getTranslateModeByType (type: number, isAICheck: boolean = false) {
        let modes: any[] = []
        if (isAICheck) {
            // translateAICheckEngines.value.forEach((item)=>{
            //     if (type == item.highLevelFlag) {
            //         modes.push(item)
            //     }
            // })
        }else {
            translateEngines.value.forEach((item)=>{
                if (type == item.highLevelFlag) {
                    modes.push(item)
                }
            })
        }
        return modes.sort((a, b) => {
            if (type == 0) {
                return a.sort - b.sort
            }else {
                return a.sort - b.sort
            }
        })
    }

    // type = 0 普通默认引擎 tpye = 1 会员默认引擎
    function getDefaultTranslateMode (type: number, isAICheck: boolean = false) {
        let defaultMode = {}
        translateEngines.value.forEach((item)=>{
            if (item.userDefaultFlag == 1 && type == 0) {
                defaultMode = item
            }else if (item.vipDefaultFlag == 1 && type == 1) {
                defaultMode = item
            }
        })
        console.log("getDefaultTranslateMode===", type, defaultMode, translateEngines.value)
        return defaultMode
    }

    // 根据当前语言获取币种
    function getCurrentCoin(lang:string) {
        const basicStore = useBasicStore()
        const { localtionData } = storeToRefs(basicStore)
        let configKey = ""
        if (lang == "zh") {
            configKey = "cny"
        }else if (lang == "en") {
            if (localtionData.value.toLowerCase() == "gb") {
                configKey = "gbp"
            }else {
                configKey = "usd"
            }
        }else if (lang == "ja") {
            configKey = "jpy"
        }else if (lang == "zh-TW") {
            if (localtionData.value.toLowerCase() == "hk") {
                configKey = "hkd"
            }else {
                configKey = "twd"
            }
        }else if (lang == "ko") {
            configKey = "krw"
        }else if (lang == "hi") {
            configKey = "usd"
        }else if (lang == "ar") {
            configKey = "usd"
        }else if (lang == "de") {
            configKey = "eur"
        }else if (lang == "fr") {
            configKey = "eur"
        }else if (lang == "id") {
            configKey = "usd"
        }else if (lang == "itb") {
            configKey = "eur"
        }else if (lang == "uk") {
            configKey = "usd"
        }else {
            configKey = "usd"
        }
        return configKey
    }

    // 根据当前语言获取币种符号
    function getCurrentLangSymbol(lang:string) {
        let coinKey = getCurrentCoin(lang)
        console.log("getCurrentLangSymbol===", coinKey, lang)
        let coin = "￥"
        if (coinKey == "cny") {
          coin = "￥"
        }else if (coinKey == "usd") {
          coin = "$"
        }else if (coinKey == "jpy") {
          coin = "円"
        }else if (coinKey == "eur") {
          coin = "€"
        }else if (coinKey == "krw") {
          coin = "₩"
        }else if (coinKey == "twd") {
          coin = "NT$"
        }else if (coinKey == "hkd") {
          coin = "HK$"
        }else if (coinKey == "gbp") {
          coin = "£"
        }
        return coin
    }

    // 根据当前币种获取币种符号
    function getCurrentCoinSymbol(coin:string) {
        let coinSymbol = "￥"
        let coinKey = coin.toLocaleLowerCase()
        if (coinKey == "cny") {
          coinSymbol = "￥"
        }else if (coinKey == "usd") {
          coinSymbol = "$"
        }else if (coinKey == "jpy") {
          coinSymbol = "円"
        }else if (coinKey == "eur") {
          coinSymbol = "€"
        }else if (coinKey == "krw") {
          coinSymbol = "₩"
        }else if (coinKey == "twd") {
          coinSymbol = "NT$"
        }else if (coinKey == "hkd") {
          coinSymbol = "HK$"
        }else if (coinKey == "gbp") {
          coinSymbol = "£"
        }
        console.log("getCurrentCoinSymbol==", coin, coinKey)
        return coinSymbol
    }

    function getUserAIChatChar() {
        let charNum = bizTeamInfo.value?.remainServiceWords || 0
        if (bizTeamInfo.value?.remainPackageWords) {
            charNum = charNum + bizTeamInfo.value?.remainPackageWords
        }
        return charNum && charNum.toLocaleString()
    }

    return {
        token, tokenFail, login,
        userInfo, getUserInfo, isUserInfoReady,
        roleRightsMap, getRoleRights,
        userRights, userRemark, getCheckUserUpload, bizTeamInfo, getDefaultTranslateMode, translateEngines, getCurrentCoinSymbol,
        getCurrentCoin, getTranslateModeByType, getTranslateEngines, getTranslateEngineCanOcr, logout, getAccountRegist, getLocalUserInfo, getBizTeamInfo, getCurrentLangSymbol
        ,getUserAIChatChar, translateAICheckEngines
    }
}, {
    persist:{
        pick: ['userInfo', 'token', 'roleRightsMap', 'translateEngines', 'translateEngineMap'],
        storage: piniaPluginPersistedstate.localStorage(),
    }
})
/** 在 setup 外使用 */
export function useUserStoreHook() {
    return useUserStore(store)
}

export default useUserStore

// function formatUseRole(userInfo: IUserInfo | null) : Roles {
//     if(!userInfo) return Roles.visitor
//     let remark = userInfo.remark && JSON.parse(userInfo.remark)
//     if(userInfo.vipLevel === 1) {
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'vip7day') return Roles.vip7day
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'vipWeek') return Roles.vipWeek
//         return Roles.svip
//     }
//     if(userInfo.vipLevel === 2) {
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'svipWeek') return Roles.svipWeek
//         return Roles.svip
//     }
//     if(userInfo.vipLevel === 0){
//         if(userInfo.validWordsForBag) return Roles.vip
//         return Roles.user
//     }
//     return Roles.user
// }

function formatUseBiz(enterpriseInfo: EnterpriseInfo | null) : Roles {
    if(!enterpriseInfo) return Roles.base
    if(enterpriseInfo?.serviceLevel === 1) {
        return Roles.base
    }
    if(enterpriseInfo?.serviceLevel === 2) {
        return Roles.middle
    }
    if(enterpriseInfo?.serviceLevel === 3){
        return Roles.high
    }
    return Roles.base
}
